import { I18nSipSetupEnum } from '../models/i18n.model';
import { isPlausiblePhoneNumber, normalizePhoneNumber } from './guided-setup.helper';

/**
 * German mobile ranges per the Bundesnetzagentur numbering plan (015x, 016x, 017x). The backend's
 * libphonenumber knows the exact allocations; these three prefixes are the unambiguous core.
 */
const MOBILE_NATIONAL_PREFIXES = ['015', '016', '017'];

/**
 * phoneservice `cliverification.blocked-number-prefixes=+4932` (application.properties, master): the
 * 032 range is nomadic and is what the SIP-Account itself signals, so it can never be the own number.
 */
const BLOCKED_NATIONAL_PREFIXES = ['032'];

/**
 * Instant feedback on a typed number, before anything is sent. Only the cases that cannot be argued
 * with: an obviously malformed entry, a foreign country code, a mobile or a 032 number. Everything
 * else - premium ranges, service numbers, ownership - is the backend's call in `number/validate`,
 * which stays authoritative; this check only saves a round trip for the common mistakes.
 *
 * @returns the i18n key of the problem, or null when the backend should decide
 */
export function precheckPhoneNumber(value: string): I18nSipSetupEnum | null {
	if (!isPlausiblePhoneNumber(value)) {
		return I18nSipSetupEnum.SIP_NUMBER_INVALID;
	}
	const national = toGermanNational(normalizePhoneNumber(value));
	if (national === 'foreign') {
		return I18nSipSetupEnum.SIP_VALIDATE_NOT_GERMAN;
	}
	if (national === null) {
		return null;
	}
	if (MOBILE_NATIONAL_PREFIXES.some((prefix) => national.startsWith(prefix))) {
		return I18nSipSetupEnum.SIP_VALIDATE_MOBILE;
	}
	if (BLOCKED_NATIONAL_PREFIXES.some((prefix) => national.startsWith(prefix))) {
		return I18nSipSetupEnum.SIP_VALIDATE_NOMADIC;
	}
	return null;
}

/**
 * `+4930…` and `004930…` become `030…`; a national `030…` stays. Another country code is 'foreign';
 * an entry without either trunk or country code is left to the backend (null).
 */
function toGermanNational(number: string): string | 'foreign' | null {
	if (number.startsWith('+')) {
		return number.startsWith('+49') ? `0${number.slice(3)}` : 'foreign';
	}
	if (number.startsWith('00')) {
		return number.startsWith('0049') ? `0${number.slice(4)}` : 'foreign';
	}
	return number.startsWith('0') ? number : null;
}
